/**
 * Utility functions for formatting LLM scan data.
 */

import type { SeverityLevel } from "@/types/llm";

/**
 * Format duration in seconds to a human-readable string (e.g., "2m 34s").
 */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === 0) {
    return "0s";
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(" ");
}

/**
 * Format ISO timestamp to a readable date string (e.g., "Dec 4, 2025 2:30 PM").
 */
export function formatTimestamp(isoString: string | null): string {
  if (!isoString) {
    return "N/A";
  }

  try {
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "Invalid date";
  }
}

/**
 * Get Tailwind CSS color class for severity level.
 */
export function getSeverityColor(severity: SeverityLevel | null | undefined): string {
  if (!severity) {
    return "bg-gray-500";
  }

  switch (severity.toLowerCase()) {
    case "critical":
      return "bg-red-600";
    case "high":
      return "bg-orange-500";
    case "medium":
      return "bg-yellow-500";
    case "low":
      return "bg-gray-400";
    default:
      return "bg-gray-500";
  }
}

/**
 * Get Tailwind CSS border color class for severity level.
 */
export function getSeverityBorderColor(severity: SeverityLevel | null | undefined): string {
  if (!severity) {
    return "border-gray-500";
  }

  switch (severity.toLowerCase()) {
    case "critical":
      return "border-red-500";
    case "high":
      return "border-orange-500";
    case "medium":
      return "border-yellow-500";
    case "low":
      return "border-gray-400";
    default:
      return "border-gray-500";
  }
}

/**
 * Get Tailwind CSS color class for scan status.
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "pending":
      return "bg-gray-500";
    case "running":
      return "bg-blue-500";
    case "completed":
      return "bg-green-500";
    case "failed":
      return "bg-red-500";
    case "cancelled":
      return "bg-yellow-500";
    default:
      return "bg-gray-500";
  }
}

/**
 * Format scan status for display (capitalize first letter).
 */
export function formatStatus(status: string): string {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

/**
 * Calculate pass rate percentage from passed and total counts.
 */
export function calculatePassRate(passed: number, total: number): number {
  if (total === 0) {
    return 100;
  }
  return Math.round((passed / total) * 100);
}

/**
 * Truncate text to a maximum length with ellipsis.
 */
export function truncateText(text: string | null | undefined, maxLength: number): string {
  if (!text) {
    return "";
  }
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength)}...`;
}

/**
 * Format pass rate as a percentage string.
 */
export function formatPassRate(passRate: number): string {
  return `${Math.round(passRate * 100)}%`;
}
